import { Dialog } from '@angular/cdk/dialog';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LearningTrailsService, RouterParamLoaderService } from '@app/core';
import { CourseContent } from '@app/core/models/course-content';
import { ReportFilterTypeEnum } from '@app/core/models/report-filter-enum';
import { CourseService } from '@app/core/services/course.service';
import { ReportFilterService } from '@app/core/services/report-filter.service';
import { Popover, Strategy } from '@app/shared/modules/template/components/popover';
import { notNull, safeEmpty, safeEmptyList, selectedEntityActive } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { ModalReportComponent } from '../../components/modal-report/modal-report.component';
import { PopoverReportFilterComponent } from '../../components/popover-report-filter/popover-report-filter.component';
import { PopoverReportComponent } from '../../components/popover-report/popover-report.component';
import { TrailsService } from '../../services';

enum StatusProgressEnum {
  Closed = 0,
  InProgress = 1,
  Future = 2,
}

export enum FilterStatusEnum {
  All = 0,
  NotEnrolled = 1,
  NotStarted = 2,
  Started = 3,
  Completed = 4,
}

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .progress-ring__circle {
      transition: stroke-dashoffset 0.35s;
      transform: rotate(-90deg);
      transform-origin: 50% 50%;
    }
    .ng-select ::ng-deep .ng-select-container span.custom {
      margin-left: 0px;
    }
  `,
  standalone: false,
})
export class ReportComponent implements OnInit {
  private readonly _dialog = inject(Dialog);
  private readonly _popover = inject(Popover);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _store = inject(Store<AppState>);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _translateService = inject(TranslateService);
  private readonly _courseService = inject(CourseService);
  private readonly _learningTrailsService = inject(LearningTrailsService);
  private readonly _reportFilterService = inject(ReportFilterService);
  private readonly _routerParamLoaderService = inject(RouterParamLoaderService);
  private readonly _cdref = inject(ChangeDetectorRef);
  public readonly trailsService = inject(TrailsService);
  public readonly StatusProgressEnum = StatusProgressEnum;
  public readonly FilterStatusEnum = FilterStatusEnum;
  public readonly strategy = Strategy;
  public readonly circumference = 251.3272;
  public readonly optionsUserStatus = [
    {
      label: 'trilhas.relatorio.user-status-options.todos',
      value: FilterStatusEnum.All,
    },
    {
      label: 'trilhas.relatorio.user-status-options.nao-matriculado',
      value: FilterStatusEnum.NotEnrolled,
    },
    {
      label: 'trilhas.relatorio.user-status-options.nao-iniciado',
      value: FilterStatusEnum.NotStarted,
    },
    {
      label: 'trilhas.relatorio.user-status-options.andamento',
      value: FilterStatusEnum.Started,
    },
    {
      label: 'trilhas.relatorio.user-status-options.concluido',
      value: FilterStatusEnum.Completed,
    },
  ];

  @Input() public subdivision: CourseContent;
  @Input() public items: CourseContent[];
  public form: FormGroup;
  public selectUserStatus = new FormControl(0);
  public searchUserByName = new FormControl(undefined);
  public searchEntityByName = new FormControl(undefined);
  public isSchoolSelected: boolean = false;
  public filterType$ = new BehaviorSubject<ReportFilterTypeEnum>(ReportFilterTypeEnum.MostCompleted);
  public showEntities: boolean = true;
  public isPopoverVisible = false;

  public courseTitle: string;
  public eventTitle: string;
  public subdivisionTitle: string;

  public popoverResult$: Observable<any>;
  public entities$: Observable<any>;
  public groups$: Observable<any>;
  public _data$ = new BehaviorSubject<any>(undefined);
  public users$: Observable<any>;
  public courses$: Observable<any>;
  public courseData$ = new BehaviorSubject<any>([]);
  public audience$ = new BehaviorSubject<any[]>([]);
  public entityType$ = new BehaviorSubject<number>(0);
  public entityActiveType$ = new BehaviorSubject<number>(0);
  public filterUserStatus$ = new BehaviorSubject<number>(FilterStatusEnum.All);
  public filterUserName$ = new BehaviorSubject<string>('');
  public hasCertificate$ = new BehaviorSubject<boolean>(false);
  public activeUser$ = new BehaviorSubject<any[]>([]);
  public token$ = new BehaviorSubject<string>(undefined);
  public loading$ = new BehaviorSubject<boolean>(false);
  public showInputOfUser: boolean = false;
  public showInputOfEntity: boolean = false;
  private lastRequestId = 0;

  public loading = {
    courses$: new BehaviorSubject<boolean>(true),
    courseData$: new BehaviorSubject<boolean>(false),
    entities$: new BehaviorSubject<boolean>(false),
    groups$: new BehaviorSubject<boolean>(false),
    audience$: new BehaviorSubject<boolean>(false),
  };

  public get events(): any {
    return this.courseData$.value?.eventos;
  }

  public get subdivisions(): any {
    return this.courseData$.value?.subdivisoes_trilhas;
  }

  public get audience(): any {
    return this.audience$.value;
  }

  public get data$(): Observable<any> {
    return this._data$.asObservable();
  }

  public set data$(value) {
    this._data$.next(value);
  }

  constructor() {
    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        notNull(),
        tap((activeUser) => this.activeUser$.next(activeUser)),
      )
      .subscribe({});

    this._store
      .select(AppSelectors.token)
      .pipe(
        take(1),
        notNull(),
        tap((token) => this.token$.next(token)),
      )
      .subscribe({});

    this._reportFilterService.filterType$.pipe(notNull()).subscribe((filterType) => {
      this.filterType$.next(filterType);
      if (filterType === ReportFilterTypeEnum.MoreRegistrations) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => b.percent_matriculados - a.percent_matriculados),
        });
      } else if (filterType === ReportFilterTypeEnum.LessRegistrations) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => a.percent_matriculados - b.percent_matriculados),
        });
      } else if (filterType === ReportFilterTypeEnum.MoreProgress) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => b.percent_iniciados - a.percent_iniciados),
        });
      } else if (filterType === ReportFilterTypeEnum.LessProgress) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => a.percent_iniciados - b.percent_iniciados),
        });
      } else if (filterType === ReportFilterTypeEnum.MostCompleted) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => b.percent_concluidos - a.percent_concluidos),
        });
      } else if (filterType === ReportFilterTypeEnum.LessCompleted) {
        this._data$.next({
          ...this._data$.value,
          entidades: this._data$.value.entidades.sort((a, b) => a.percent_concluidos - b.percent_concluidos),
        });
      }

      this._cdref.markForCheck();
    });
  }

  public ngOnInit(): void {
    this.initForm();

    this.courses$ = this._store.select(AppSelectors.ActiveUser).pipe(
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      tap((user) => this.entityActiveType$.next(selectedEntityActive(user)[0].tipo)),
      switchMap((user) => this._learningTrailsService.showcaseForReport(selectedEntityActive(user)[0])),
      safeEmptyList(),
      map((courses: any[]) =>
        courses.map((course) => ({
          ...course,
          courseTitle: course.colecao?.nome ? `${course.titulo} - ${course.colecao.nome}` : course.titulo,
        })),
      ),
      tap((data: any) => {
        const course = data.find((course) => course.slug === this._activatedRoute.snapshot.params.course_slug);

        this.courseTitle = course?.courseTitle ?? null;
      }),
      tap(() => this.loading.courses$.next(false)),
    );

    const courseSlug = this._activatedRoute.snapshot.params.course_slug;
    if (!!courseSlug) {
      this.form.controls.course.setValue(courseSlug, { emitEvent: false });
      this.loadEvents(courseSlug);
    }
  }

  private initForm(): void {
    this.form = this._formBuilder.group({
      course: [undefined, Validators.required],
      event: [undefined, Validators.required],
      entities: [[], Validators.required],
      group: [{ value: undefined, disabled: true }],
      audience: [[]],
      item: [undefined],
    });

    this.form.controls.course.valueChanges.pipe(takeUntilDestroyed(this._destroyRef), notNull()).subscribe((slug) => {
      this.adjustUrl(slug);
      this.form.patchValue({ event: undefined, entities: undefined, group: undefined, item: undefined });
      this.form.markAsUntouched();
      this.loadEvents(slug);
    });

    this.form.controls.event.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), notNull())
      .subscribe((eventSelected) => {
        if (this.trailsService.uc$.value || this.trailsService.ivca$.value) {
          setTimeout(() => {
            this.loadAudience();
          }, 0);
        }

        if (!!eventSelected?.certificado) {
          this.hasCertificate$.next(true);
        } else {
          this.hasCertificate$.next(false);
        }
        this.form.patchValue({ entities: undefined, group: undefined, item: undefined }, { emitEvent: false });
        this.form.markAsUntouched();
        if (!!eventSelected) {
          this.loadEntities(eventSelected.id);
          this.eventTitle = this.events.find((event) => event.id === eventSelected.id).titulo;
        }
      });

    this.form.controls.entities.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged())
      .subscribe((entities) => {
        this.form.patchValue({ group: undefined }, { emitEvent: false });
        this.form.markAsUntouched();

        if (entities.length > 0) {
          this.entityType$.next(entities[0].tipo);

          const isSchool = this.entityType$.value === 1;
          this.showEntities = !isSchool;
          this.isSchoolSelected = isSchool;

          if (isSchool) {
            this.isSchoolSelected = true;
            setTimeout(() => {
              this.loadUsers(entities);
              if (!this.trailsService.uc$.value || !this.trailsService.ivca$.value) {
                this.loadGroups(entities);
              }
            }, 0);
          } else {
            this.users$ = of(null);

            this.groups$ = of([]);
            this.form.controls.group.disable({ emitEvent: false });
            this.form.controls.group.setValue(undefined, { emitEvent: false });
          }
        } else {
          this.entityType$.next(undefined);
          this.users$ = of(null);

          this.groups$ = of([]);
          this.form.controls.group.disable({ emitEvent: false });
          this.form.controls.group.setValue(undefined, { emitEvent: false });
        }

        this._data$.next(undefined);
        this.loadData();
      });

    this.form.controls.group.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged())
      .subscribe(() => {
        this.users$ = of(null);
        this.loadUsers(this.form.controls.entities.value);
        this._data$.next(undefined);
        this.loadData();
      });

    this.form.controls.audience.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((data) => {
        this.eventTitle = data.label;
        this.users$ = of(null);

        if (this.entityType$.value === 1) {
          setTimeout(() => {
            this.loadUsers(this.form.controls.entities.value);
          }, 0);
        }

        this._data$.next(undefined);
        setTimeout(() => {
          this.loadData();
        }, 0);
      });

    this.form.controls.item.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef), distinctUntilChanged())
      .subscribe((data) => {
        if (data) {
          this.eventTitle = data.type === 1 ? data.value.nome : data.value.titulo;
        }

        this.users$ = of(null);

        if (this.entityType$.value === 1) {
          this.loadUsers(this.form.controls.entities.value);
        }

        this._data$.next(undefined);
        this.loadData();
      });

    this.selectUserStatus.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((status) => {
      this.filterUserStatus$.next(status);
    });

    this.searchUserByName.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((name) => {
      this.filterUserName$.next(name);
    });
  }

  private loadEvents(courseSlug: string): void {
    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        tap(() => this.loading.courseData$.next(true)),
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        switchMap((user) => this._learningTrailsService.courseReportData(courseSlug, selectedEntityActive(user)[0])),
        safeEmptyList(),
        tap(() => this.loading.courseData$.next(false)),
        tap((data: any) => {
          if (data.eventos.length === 1) {
            this.form.controls.event.setValue(data.eventos[0], { emitEvent: false });
            this.loadEntities(data.eventos[0].id);
            this.eventTitle = data.eventos[0].titulo;
            this.loadAudience();
          }
        }),
      )
      .subscribe((data) => this.courseData$.next(data));
  }

  private loadEntities(eventId: number): void {
    const allEntities: Observable<any> = this._store.select(AppSelectors.ActiveUser).pipe(
      tap(() => this.loading.entities$.next(true)),
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      tap((user) => this.entityType$.next(user.tipo)),
      switchMap((user) => this._learningTrailsService.eventEntities(eventId, selectedEntityActive(user)[0]).pipe()),
      safeEmptyList(),
      tap(() => this.loading.entities$.next(false)),
    );

    const entities: Observable<any> = combineLatest([allEntities, this._store.select(AppSelectors.ActiveUser)]).pipe(
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      tap(([entities, user]) => {
        const [currentEntity] = selectedEntityActive(user);
        const { id: currentEntityId, tipo: currentEntityType } = currentEntity;

        const matchingEntity = entities.find(
          (entity) => entity.id === currentEntityId && entity.tipo === currentEntityType,
        );

        if (matchingEntity) {
          this.form.controls.entities.setValue([matchingEntity]);
        }
      }),
      map(([entities]) => entities),
    );

    this.entities$ = combineLatest([entities, this.entityType$]).pipe(
      map(([entities, entityType]) => {
        if (entityType) {
          const filteredEntities = entities.filter((entity) => entity.tipo === entityType);
          return filteredEntities;
        }
        return entities;
      }),
    );
  }

  private loadGroups(entities: any): void {
    this.groups$ = this._store.select(AppSelectors.appFeature).pipe(
      tap(() => this.loading.groups$.next(true)),
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      filter(({ funcionario_id, funcao_padrao }) => !!funcionario_id && !!funcao_padrao),
      switchMap(({ funcionario_id, funcao_padrao }) =>
        this._learningTrailsService.entityGroups(funcionario_id, funcao_padrao, entities),
      ),
      safeEmptyList(),
      tap(() => {
        this.loading.groups$.next(false);
        this.form.controls.group.enable({ emitEvent: false });
      }),
    );
  }

  public loadAudience() {
    this._learningTrailsService
      .eventDetails(this.form.controls.event.value.id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        finalize(() => {
          this.loading.audience$.next(false);
        }),
      )
      .subscribe({
        next: (resp) => {
          const audience = resp.data.publico_alvo.map((item: any) => {
            const complemento = item.entidade || item.campo || item.uniao || item.editora || item.divisao;

            return {
              ...item,
              label: complemento ? `${item.nome} - ${complemento}` : item.nome,
            };
          });

          this.audience$.next(audience);
        },
      });
  }

  private loadData(): void {
    if (this.form.invalid) {
      return;
    }

    const currentRequestId = ++this.lastRequestId;

    let item = undefined;
    if (this.form.controls.item.value) {
      item = { id: this.form.controls.item.value.value.id, type: this.form.controls.item.value.type };
    }

    if (!!this.form.value.course) {
      this._learningTrailsService
        .loadReportData(
          this.form.value.course,
          this.form.value.event?.id,
          this.form.value.entities,
          this.form.value.group,
          item,
          this.form.value.audience,
        )
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          notNull(),
          safeEmpty(),
          mergeMap(async (data) => {
            if (currentRequestId === this.lastRequestId) {
              return data;
            }
            return null;
          }),
        )
        .subscribe((data) => {
          if (data) {
            this._data$.next(data);
          }
        });
    }
  }

  private loadUsers(entities: any): void {
    let item = undefined;
    if (this.form.controls.item.value) {
      item = { id: this.form.controls.item.value.value.id, type: this.form.controls.item.value.type };
    }

    const users: Observable<any> = this._learningTrailsService
      .loadReportUsers(
        this.form.value.course,
        this.form.value.event?.id,
        entities,
        this.form.value.group,
        item,
        this.form.value.audience,
      )
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), safeEmptyList());

    this.users$ = combineLatest([users, this.filterUserStatus$, this.filterUserName$]).pipe(
      takeUntilDestroyed(this._destroyRef),
      notNull(),
      map(([users, status, name]) => {
        const { entidade, exige_voucher, users: usersList } = users;

        const filterOptions = {
          [FilterStatusEnum.All]: () => usersList,
          [FilterStatusEnum.NotEnrolled]: () =>
            usersList.filter((user) => user.status === FilterStatusEnum.NotEnrolled),
          [FilterStatusEnum.NotStarted]: () => usersList.filter((user) => user.status === FilterStatusEnum.NotStarted),
          [FilterStatusEnum.Started]: () => usersList.filter((user) => user.status === FilterStatusEnum.Started),
          [FilterStatusEnum.Completed]: () => usersList.filter((user) => user.status === FilterStatusEnum.Completed),
        };

        if (name) {
          return {
            entidade,
            exige_voucher,
            users: filterOptions[status]().filter(
              (user) => user.nome && user.nome.toLowerCase().includes(name.toLowerCase()),
            ),
          };
        }

        return { entidade, exige_voucher, users: filterOptions[status]() };
      }),
    );
  }

  public onOpenModal() {
    const eventPublic: Observable<any> = this._learningTrailsService
      .eventDetails(this.form.controls.event.value.id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        safeEmpty(),
        notNull(),
        map(({ publico_alvo, publico_alvo_usuarios }) => [...publico_alvo, ...publico_alvo_usuarios]),
      );
    this._dialog.open<boolean>(ModalReportComponent, { data: eventPublic }).closed.pipe(notNull());
  }

  public onOpenPopover(elementRef: ElementRef, positionStrategy: ConnectionPositionPair[] = [], data: any): void {
    let notEnrolled = 0;
    let started = 0;
    let completed = 0;
    let notStarted = 0;
    if (data.entidade[0].tipo !== 1) {
      notEnrolled = data.entidade[0].total - data.entidade[0].total_matriculados;
      started = data.entidade[0].total_iniciados;
      completed = data.entidade[0].total_concluidos;
      notStarted = data.entidade[0].total_matriculados - (started + completed);
    } else {
      notEnrolled = data.users.filter((user) => user.status === FilterStatusEnum.NotEnrolled).length;
      started = data.users.filter((user) => user.status === FilterStatusEnum.Started).length;
      completed = data.users.filter((user) => user.status === FilterStatusEnum.Completed).length;
      notStarted = data.users.filter((user) => user.status === FilterStatusEnum.NotStarted).length;
    }

    this.popoverResult$ = this._popover.open(PopoverReportComponent, {
      positionStrategy,
      elementRef,
      data: {
        chartData: [
          {
            name: this._translateService.instant('trilhas.relatorio.user-status-options.nao-matriculado'),
            value: notEnrolled,
          },
          {
            name: this._translateService.instant('trilhas.relatorio.user-status-options.nao-iniciado'),
            value: notStarted,
          },
          {
            name: this._translateService.instant('trilhas.relatorio.user-status-options.andamento'),
            value: started,
          },
          {
            name: this._translateService.instant('trilhas.relatorio.user-status-options.concluido'),
            value: completed,
          },
        ],
        excelData: {
          courseTitle: this.courseTitle,
          eventTitle: this.eventTitle,
          subdivisionTitle: this.subdivisionTitle,
          data$: this.users$,
        },
      },
    }).closed;
  }

  public onOpenFilterPopover(
    elementRef: ElementRef,
    positionStrategy: ConnectionPositionPair[] = [],
    typeFilter: ReportFilterTypeEnum,
  ): void {
    this.isPopoverVisible = !this.isPopoverVisible;
    const popover = this._popover.open(PopoverReportFilterComponent, {
      positionStrategy,
      elementRef,
      data: { typeFilter, auto_matricula: this._data$.value.auto_matricula },
    });

    popover.closed
      .pipe(
        tap(() => {
          this.isPopoverVisible = !this.isPopoverVisible;
          this._cdref.markForCheck();
        }),
      )
      .subscribe();
  }

  public onGetStatusEventLabel(status: number): string {
    const options = {
      [StatusProgressEnum.Closed]: () => this._translateService.instant('trilhas.relatorio.evento-status.encerrado'),
      [StatusProgressEnum.InProgress]: () =>
        this._translateService.instant('trilhas.relatorio.evento-status.andamento'),
      [StatusProgressEnum.Future]: () => this._translateService.instant('trilhas.relatorio.evento-status.previsto'),
    };

    return options[status]();
  }

  public onStokeDashoffset(progress: number): number {
    return this.circumference - (progress / 100) * this.circumference;
  }

  public onChangeEntityReference(entity: any): void {
    this.form.controls.entities.setValue([entity]);
  }

  public onClickEntity(entity: any): void {
    this.isSchoolSelected = true;
    this.loadUsers([entity]);
  }

  public onRedeemCertificate(userUuid: string) {
    this._courseService.printCertificate(
      this.form.value.event.id,
      this.form.value.course,
      this.token$.value,
      userUuid,
      this.activeUser$.value,
    );
  }

  public onStateCertificate(user: any): string {
    if (user.data_impressao) {
      return `${this._translateService.instant('trilhas.relatorio.certificado-resgatado')} ${format(
        new Date(user.data_impressao),
        'dd/MM/yyyy',
      )}`;
    }
    return this._translateService.instant('trilhas.relatorio.certificado-disponivel');
  }

  private adjustUrl(course_slug: string): void {
    this._routerParamLoaderService.updateUrl({ course_slug });
  }

  public onToggleInputOfUser() {
    this.showInputOfUser = !this.showInputOfUser;
  }

  public onToggleInputOfEntity() {
    this.showInputOfEntity = !this.showInputOfEntity;
  }

  public checkActiveEntityType() {
    return this.entityActiveType$.value !== 1;
  }
}
